import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ToggleLeft,
  ToggleRight,
  Sparkles,
  Heart,
  MessageSquare,
  Calendar,
  ListChecks,
  Baby,
  Shield,
  RefreshCw,
  Timer,
  FileText,
  ScanBarcode,
  Package,
  CreditCard,
  Smartphone,
  Moon,
  Pill,
  AudioLines,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

interface FeatureToggle {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  category: string;
}

const FEATURE_TOGGLES: FeatureToggle[] = [
  {
    key: 'feature_ai_chat',
    label: 'AI-Chat',
    description: 'Ermöglicht Nutzern, mit dem AI-Assistenten zu chatten',
    icon: <Sparkles className="w-5 h-5 text-emerald-500" />,
    category: 'AI-Features',
  },
  {
    key: 'feature_name_swipe',
    label: 'Namen-Swipe',
    description: 'Tinder-ähnliches Swipe-Feature für Babynamen',
    icon: <Heart className="w-5 h-5 text-rose-500" />,
    category: 'Namen',
  },
  {
    key: 'feature_name_suggestions',
    label: 'Community-Vorschläge',
    description: 'Nutzer können neue Namen vorschlagen',
    icon: <MessageSquare className="w-5 h-5 text-blue-500" />,
    category: 'Namen',
  },
  {
    key: 'feature_appointments',
    label: 'Terminverwaltung',
    description: 'Verwaltung von Arzt- und Vorsorge-Terminen',
    icon: <Calendar className="w-5 h-5 text-amber-500" />,
    category: 'Kalender',
  },
  {
    key: 'feature_contraction_counter',
    label: 'Wehenzähler',
    description: 'Erfassung und Analyse von Wehen mit Partner-Benachrichtigungen',
    icon: <Timer className="w-5 h-5 text-red-500" />,
    category: 'Gesundheit',
  },
  {
    key: 'feature_checklists',
    label: 'Checklisten',
    description: 'Vorgefertigte und eigene Checklisten',
    icon: <ListChecks className="w-5 h-5 text-purple-500" />,
    category: 'Organisation',
  },
  {
    key: 'feature_product_check',
    label: 'Produktcheck',
    description: 'Überprüfung von Produkten auf Schwangerschaftsverträglichkeit',
    icon: <Package className="w-5 h-5 text-orange-500" />,
    category: 'Tools',
  },
  {
    key: 'feature_barcode_scanner',
    label: 'Barcode-Scanner',
    description: 'Scannen von Produkten per Kamera (nur native Apps)',
    icon: <ScanBarcode className="w-5 h-5 text-indigo-500" />,
    category: 'Tools',
  },
  {
    key: 'feature_authority_guides',
    label: 'Behördenguides',
    description: 'Anleitungen für Behördengänge (Elterngeld, Geburtsurkunde, etc.)',
    icon: <FileText className="w-5 h-5 text-cyan-500" />,
    category: 'Guides',
  },
  {
    key: 'feature_pregnancy_card',
    label: 'Schwangerschafts-Infokarte',
    description: 'Digitale Infokarte mit wichtigen Schwangerschaftsdaten',
    icon: <CreditCard className="w-5 h-5 text-pink-500" />,
    category: 'Tools',
  },
  {
    key: 'feature_partner_sync',
    label: 'Partner-Synchronisation',
    description: 'Partner können verbunden werden und Daten teilen',
    icon: <Baby className="w-5 h-5 text-teal-500" />,
    category: 'Partner',
  },
  {
    key: 'feature_data_export',
    label: 'Datenexport',
    description: 'Nutzer können ihre Daten exportieren',
    icon: <Shield className="w-5 h-5 text-slate-500" />,
    category: 'Datenschutz',
  },
  {
    key: 'baby_care_enabled',
    label: 'Baby-Care-Koordination',
    description: 'Aktiviert das Baby-Pflege-Tracking für Nutzer im Baby-Modus (nach der Geburt)',
    icon: <Baby className="w-5 h-5 text-pink-500" />,
    category: 'Baby-Modus',
  },
  {
    key: 'sleep_plan_enabled',
    label: 'Schlafplan',
    description: 'Wake-Window-basierter Schlafplan mit Benachrichtigungen und Analyse für den Baby-Modus',
    icon: <Moon className="w-5 h-5 text-indigo-500" />,
    category: 'Baby-Modus',
  },
  {
    key: 'medication_plan_enabled',
    label: 'Med-Plan',
    description: 'Medikamenten-Erinnerungen und -Tracking im Baby-Modus',
    icon: <Pill className="w-5 h-5 text-red-500" />,
    category: 'Baby-Modus',
  },
  {
    key: 'sleeping_sounds_enabled',
    label: 'Sleeping Sounds',
    description: 'Einschlaf-Sounds mit Timer und Fade-Out für den Baby-Modus',
    icon: <AudioLines className="w-5 h-5 text-teal-500" />,
    category: 'Baby-Modus',
  },
  {
    key: 'feature_native_iap',
    label: 'Native In-App-Käufe',
    description: 'In-App-Käufe über App Store / Google Play (wenn deaktiviert: Web-Fallback mit Stripe)',
    icon: <Smartphone className="w-5 h-5 text-emerald-500" />,
    category: 'Zahlungen',
  },
];

export function FeatureTogglesPage() {
  const queryClient = useQueryClient();

  // Fetch current feature toggle settings
  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ['feature-toggles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .or('key.like.feature_%,key.like.baby_%_enabled,key.like.sleep_%_enabled,key.like.medication_%_enabled,key.like.sleeping_%_enabled');

      if (error) throw error;

      const settingsMap: Record<string, boolean> = {};
      (data as { key: string; value: unknown }[])?.forEach(s => {
        settingsMap[s.key] = s.value === true || s.value === 'true';
      });

      return settingsMap;
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      // Try to update first
      const { data: existing } = await supabase
        .from('system_settings')
        .select('key')
        .eq('key', key)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('system_settings')
          .update({ value: enabled, updated_at: new Date().toISOString() } as never)
          .eq('key', key);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('system_settings')
          .insert({ key, value: enabled } as never);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-toggles'] });
      toast.success('Feature-Status aktualisiert');
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const groupedFeatures = FEATURE_TOGGLES.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, FeatureToggle[]>);

  const getToggleState = (key: string) => {
    return settings?.[key] !== false; // Default to enabled if not set
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Feature-Toggles</h1>
            <p className="text-slate-600">Features aktivieren oder deaktivieren</p>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
            title="Aktualisieren"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Einstellungen...</div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFeatures).map(([category, features]) => (
            <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">{category}</h2>
              </div>
              <div className="divide-y divide-slate-200">
                {features.map((feature) => {
                  const isEnabled = getToggleState(feature.key);
                  return (
                    <div key={feature.key} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          {feature.icon}
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900">{feature.label}</h3>
                          <p className="text-sm text-slate-500">{feature.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleMutation.mutate({ key: feature.key, enabled: !isEnabled })}
                        disabled={toggleMutation.isPending}
                        className="focus:outline-none"
                      >
                        {isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-teal-500" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-slate-300" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Hinweis</h3>
            <p className="text-sm text-amber-700 mt-1">
              Das Deaktivieren eines Features hat sofortige Auswirkung auf alle Nutzer.
              Nutzer sehen das Feature nicht mehr und können es nicht verwenden.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
